// Structured Investment Thesis renderer.
//
// Reads the JSON shape produced by the api-server thesis-parser and draws
// a positioning paragraph, a Funding card, a Traction card, and a Risks
// pill list. When `structured` is null/empty, callers should render the
// raw `thesis` paragraph as a fallback (this component returns null in
// that case so callers don't accidentally render an empty frame).

import * as React from "react";
import { Briefcase, TrendingUp, AlertTriangle } from "lucide-react";

export interface StructuredThesisData {
  positioning: string | null;
  funding: {
    raised: string | null;
    valuation: string | null;
    seeking: string | null;
    notes: string | null;
  };
  traction: {
    revenue: string | null;
    growth: string | null;
    customers: string | null;
    keyDrivers: string[];
  };
  risks: string[];
}

function isMeaningful(structured: StructuredThesisData | null | undefined): boolean {
  if (!structured) return false;
  const f = structured.funding || ({} as StructuredThesisData["funding"]);
  const t = structured.traction || ({} as StructuredThesisData["traction"]);
  if (structured.positioning) return true;
  if (f.raised || f.valuation || f.seeking || f.notes) return true;
  if (t.revenue || t.growth || t.customers || (t.keyDrivers && t.keyDrivers.length > 0)) return true;
  if (structured.risks && structured.risks.length > 0) return true;
  return false;
}

interface Props {
  structured: StructuredThesisData | null | undefined;
  // Theming — investor portal uses dark surfaces; microsite uses light.
  variant?: "dark" | "light";
}

export function StructuredThesis({ structured, variant = "dark" }: Props): React.JSX.Element | null {
  if (!isMeaningful(structured)) return null;
  const s = structured!;

  // CONTRAST CONTRACT — every value/body text class on the dark variant
  // must stay at >= text-white/85 so it remains legible on the
  // bg-gradient-to-br from-[#1A1A1A] via-[#2D1810] to-[#1A1A1A] surface.
  // Labels/captions can sit at /70. The contrast linter in
  // scripts/check-contrast.mjs enforces this — do not weaken these values.
  const cardClass = variant === "dark"
    ? "bg-black/40 rounded-xl p-4 border border-white/15"
    : "bg-muted/40 rounded-xl p-4 border border-border";
  const labelClass = "text-[#FF5C00] font-semibold text-xs uppercase tracking-wider";
  const itemKeyClass = variant === "dark"
    ? "text-white/70 text-[11px] uppercase tracking-wider font-medium"
    : "text-muted-foreground text-[11px] uppercase tracking-wider";
  const itemValClass = variant === "dark"
    ? "text-white text-sm font-semibold"
    : "text-foreground text-sm font-medium";
  const positioningClass = variant === "dark"
    ? "text-white/95 text-sm leading-relaxed font-normal"
    : "text-foreground text-sm leading-relaxed";
  const driverClass = variant === "dark"
    ? "text-white/85 text-xs leading-relaxed"
    : "text-muted-foreground text-xs";
  const riskPillClass = variant === "dark"
    ? "inline-flex items-center px-2.5 py-1 text-[11px] rounded-full bg-amber-500/20 border border-amber-400/50 text-amber-100 font-medium"
    : "inline-flex items-center px-2 py-0.5 text-[11px] rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-700";

  const fundingItems: Array<[string, string]> = [];
  if (s.funding.raised) fundingItems.push(["Raised", s.funding.raised]);
  if (s.funding.valuation) fundingItems.push(["Valuation", s.funding.valuation]);
  if (s.funding.seeking) fundingItems.push(["Seeking", s.funding.seeking]);

  const tractionItems: Array<[string, string]> = [];
  if (s.traction.revenue) tractionItems.push(["Revenue", s.traction.revenue]);
  if (s.traction.growth) tractionItems.push(["Growth", s.traction.growth]);
  if (s.traction.customers) tractionItems.push(["Customers", s.traction.customers]);

  return (
    <div className="space-y-4">
      {s.positioning && <p className={positioningClass}>{s.positioning}</p>}

      <div className="grid gap-3 md:grid-cols-2">
        {(fundingItems.length > 0 || s.funding.notes) && (
          <div className={cardClass}>
            <div className="flex items-center gap-2 mb-3">
              <Briefcase className="w-4 h-4 text-[#FF5C00]" />
              <span className={labelClass}>Funding</span>
            </div>
            <div className="space-y-2">
              {fundingItems.map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between gap-3">
                  <span className={itemKeyClass}>{k}</span>
                  <span className={`${itemValClass} text-right`}>{v}</span>
                </div>
              ))}
              {s.funding.notes && (
                <p className={`${driverClass} pt-1 border-t ${variant === "dark" ? "border-white/10" : "border-border"} mt-2`}>
                  {s.funding.notes}
                </p>
              )}
            </div>
          </div>
        )}

        {(tractionItems.length > 0 || s.traction.keyDrivers.length > 0) && (
          <div className={cardClass}>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-[#FF5C00]" />
              <span className={labelClass}>Traction</span>
            </div>
            <div className="space-y-2">
              {tractionItems.map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between gap-3">
                  <span className={itemKeyClass}>{k}</span>
                  <span className={`${itemValClass} text-right`}>{v}</span>
                </div>
              ))}
              {s.traction.keyDrivers.length > 0 && (
                <ul className={`${driverClass} pt-1 mt-2 border-t ${variant === "dark" ? "border-white/10" : "border-border"} list-disc pl-4 space-y-1`}>
                  {s.traction.keyDrivers.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      {s.risks.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className={labelClass}>Risks</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {s.risks.map((r, i) => <span key={i} className={riskPillClass}>{r}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

export function hasStructuredThesis(structured: StructuredThesisData | null | undefined): boolean {
  return isMeaningful(structured);
}
